import { z } from "zod";

/** Ligne des listes dynamiques terrasses/chambres (fiche VENTE, mig 096). */
const SurfaceOrientationSchema = z.object({
  surface: z.string(),
  orientation: z.string(),
});

/**
 * Schéma Zod pour l'entité Bien.
 *
 * Permissif par design : les fonctions RPC (get_bien_for_role, etc.)
 * retournent des objets *partiels* pour les non-admins — seules les colonnes
 * autorisées par `field_permissions.can_see` sont présentes. On valide donc
 * l'ID comme obligatoire et tout le reste comme optionnel.
 *
 * La validation ne sert pas à *rejeter* les données (les RPC ont déjà filtré),
 * mais à catcher des divergences de schéma entre DB et front (ex. renommage
 * de colonne oublié).
 */
export const BienSchema = z.object({
  id: z.string(),
  reference: z.string().optional(),
  // .nullable() car les imports Immoweb (location + vente) écrivent NULL en
  // l'absence de correspondance catalogue plutôt qu'une chaîne vide.
  referenceInterne: z.string().nullable().optional(),
  referenceExterne: z.string().optional(),
  codeImmoweb: z.string().optional(),
  projet: z.string().nullable().optional(),
  typeBien: z.string().optional(),
  transaction: z.enum(["location", "vente"]).nullable().optional(),
  disponibilite: z.string().optional(),
  seraDispoPour: z.string().optional(),
  agent: z.string().optional(),
  adresse: z.string().optional(),
  codePostal: z.string().optional(),
  ville: z.string().optional(),
  region: z.string().optional(),
  quartier_id: z.string().nullable().optional(),
  etage: z.string().optional(),
  etagesTotal: z.string().optional(),
  typeEnvironnement: z.string().optional(),
  propertyBox: z.string().nullable().optional(),
  meuble: z.boolean().nullable().optional(),
  surfaceHabitable: z.string().optional(),
  surfaceBrute: z.string().optional(),
  surfaceSalon: z.string().optional(),
  // .nullable() car les imports Immoweb écrivent NULL quand "Surface de la
  // cuisine" est absente de l'annonce.
  surfaceCuisine: z.string().nullable().optional(),
  nbChambres: z.string().optional(),
  nbSdb: z.string().optional(),
  // .nullable() car les imports Immoweb écrivent NULL quand la ligne "Salles
  // de douche" est absente de l'annonce (≠ "Salles de bains").
  nbDouches: z.string().nullable().optional(),
  nbToilettes: z.string().optional(),
  typeSol: z.string().optional(),
  surfaceChambre1: z.string().optional(),
  surfacesChambres: z.array(z.string()).nullable().optional(),
  equipements: z.array(z.string()).optional(),
  surfaceTerrasse: z.string().optional(),
  surfaceJardin: z.string().optional(),
  caveDisponible: z.string().optional(),
  caveNumero: z.string().nullable().optional(),
  parkingDisponible: z.string().nullable().optional(),
  parkingNumero: z.string().nullable().optional(),
  parkingFerme: z.boolean().nullable().optional(),
  parkingInterieur: z.boolean().nullable().optional(),
  // Garage (distinct du parking) — migration 062. .nullable() car rows existantes = NULL.
  garageDisponible: z.string().nullable().optional(),
  garageNumero: z.string().nullable().optional(),
  loyerMensuel: z.string().optional(),
  chargesMensuelles: z.string().optional(),
  parkingLoyerMensuel: z.string().optional(),
  caveLoyerMensuel: z.string().optional(),
  garageLoyerMensuel: z.string().nullable().optional(),
  chargesIncluses: z.array(z.string()).optional(),
  prix: z.string().optional(),
  surface: z.string().optional(),
  classePeb: z.string().optional(),
  // .nullable() car les imports Immoweb écrivent NULL quand le "numéro" PEB
  // n'est en réalité que la lettre de classe répétée (pas un vrai numéro).
  certificatPeb: z.string().nullable().optional(),
  consoKwh: z.string().optional(),
  emissionCo2: z.string().nullable().optional(),
  pebFinLe: z.string().optional(),
  expertPeb: z.string().optional(),
  // Mission PEB — date/heure du rdv prestataire PEB (spec user 2026-05-23).
  // .nullable() obligatoire car rows existantes renvoient NULL.
  pebDatePrevue: z.string().nullable().optional(),
  pebHeure: z.string().nullable().optional(),
  pebCommentaire: z.string().nullable().optional(),
  pebCertificatPdf: z.string().nullable().optional(),
  // Statut PEB — suit le même précédent que statutEle : simple .optional(),
  // la colonne est NOT NULL avec default '' côté DB donc pas de NULL entrant.
  statutPeb: z.string().nullable().optional(),
  // Champs nouveau certificat (staging) — migration 2026-06-02.
  // .nullable().optional() obligatoire car rows existantes renvoient NULL.
  nouvClassePeb: z.string().nullable().optional(),
  nouvCertificatPeb: z.string().nullable().optional(),
  nouvPebFinLe: z.string().nullable().optional(),
  nouvConsoKwh: z.string().nullable().optional(),
  nouvEmissionCo2: z.string().nullable().optional(),
  // Ajoutés 2026-09-12 : consoTheorique/chauffageEnergie n'avaient aucun
  // point de saisie pendant un renouvellement en cours (cf. promotePebCycle).
  nouvConsoTheorique: z.string().nullable().optional(),
  nouvChauffageEnergie: z.string().nullable().optional(),
  nouvPebCertificatPdf: z.string().nullable().optional(),
  // .nullable() car les imports Immoweb écrivent NULL quand l'année n'est
  // pas publiée sur l'annonce.
  anneeConstruction: z.string().nullable().optional(),
  anneeRenovation: z.string().optional(),
  nbFacades: z.string().optional(),
  largeurFacade: z.string().nullable().optional(),
  etatBatiment: z.string().optional(),
  renonRecuLe: z.string().optional(),
  // Cycle de sortie ouvert le… (migration 135, plan « deux cycles » Phase 2) :
  // horodatage du geste « Ouvrir un cycle », NULL = aucun cycle. .nullable()
  // obligatoire : toutes les rows existantes renvoient NULL.
  cycleOuvertLe: z.string().nullable().optional(),
  // Entrée en gestion : 'rotation' (locataire sortant, défaut) | 'bien_vide'
  // (neuf / reprise sans locataire — pas de renon ni de cycle ELS de sortie).
  // nullable : rows créées avant la migration 081 renvoient NULL.
  entreeGestion: z.string().nullable().optional(),
  finDeContrat: z.string().optional(),
  // Sous option (migration 084) — overlay booléen : bien en négociation retiré
  // de la vitrine (sites + Immoweb + mailing) sans quitter le statut DISPONIBLE.
  // .nullable().optional() car rows existantes renvoient NULL.
  sousOption: z.boolean().nullable().optional(),
  sousOptionDepuis: z.string().nullable().optional(),
  // Statut de publication mémorisé AVANT la mise sous option (interne) : sert à
  // ne republier au décochage que si c'est la mise sous option qui avait dépublié.
  sousOptionStatutAvant: z.string().nullable().optional(),
  companyContract: z.boolean().nullable().optional(),
  collaborateurSrs: z.string().optional(),
  prestataireEls: z.string().nullable().optional(),
  prestataireEle: z.string().optional(),
  edlDatePrevue: z.string().optional(),
  edlHeure: z.string().optional(),
  edlCommentaire: z.string().nullable().optional(),
  statutEle: z.string().optional(),
  statutEls: z.string().optional(),
  eleNonFait: z.boolean().optional(),
  ancienPrestataireEls: z.string().optional(),
  // Migration 055 : trace du refus prestataire via "Je n'ai pas fait l'ELE".
  // .nullable().optional() car rows existantes renvoient NULL.
  prestataireEleRefusePar: z.string().nullable().optional(),
  prestataireEleRefuseAt: z.string().nullable().optional(),
  // Migration 068 : trace du refus de l'expert PEB (symétrique ELS).
  expertPebRefusePar: z.string().nullable().optional(),
  expertPebRefuseAt: z.string().nullable().optional(),
  appartPretPhotos: z.boolean().nullable().optional(),
  /** Un ELS est-il requis sur ce cycle ? Migration 106, NOT NULL DEFAULT true
   *  en base — `.nullable().optional()` couvre le cas où la RPC strippe la
   *  colonne pour un rôle sans droit de lecture. */
  elsRequis: z.boolean().nullable().optional(),
  /** Une expertise PEB est-elle demandée sur ce cycle ? Décidée au même
   *  moment que `elsRequis`, dans le dialogue d'ouverture de cycle — même
   *  contrat NOT NULL DEFAULT true (migration à venir). */
  pebRequis: z.boolean().nullable().optional(),
  transfertElecFait: z.boolean().optional(),
  // Migration 048 (2026-05-20) : DATE dédiée saisie via EtatDesLieuxDetailPage.
  // Sans cette ligne, Zod strippe la clé sur READ et WRITE → la date n'arrive
  // jamais en DB et la pastille Élec (qui lit ce champ via isTransfertElecFait)
  // reste rouge même après saisie. .nullable() obligatoire car rows existantes
  // renvoient NULL.
  transfertElecDate: z.string().nullable().optional(),
  // Migration 090 (2026-07-11) : horodate du lancement manuel des missions
  // prestataire (ELS/PEB). NULL = non lancée. Posées serveur par l'edge
  // function launch-mission. .nullable().optional() obligatoire (rows existantes = NULL).
  elsMissionLanceeAt: z.string().nullable().optional(),
  pebMissionLanceeAt: z.string().nullable().optional(),
  dossierCloture: z.boolean().optional(),
  dossierClos: z.boolean().optional(),
  // Finalisation dossier SRS
  montantPreavis: z.string().nullable().optional(),
  montantDommages: z.string().nullable().optional(),
  arrieresLoyer: z.string().nullable().optional(),
  montantEls: z.string().nullable().optional(),
  decompteCharges: z.string().nullable().optional(),
  autreMontant: z.string().nullable().optional(),
  indemnitePreavis: z.string().nullable().optional(),
  garantieLiberee: z.string().nullable().optional(),
  totalSrs: z.string().nullable().optional(),
  commentaireSrs: z.string().nullable().optional(),
  statutPublication: z.string().optional(),
  photosOk: z.string().optional(),
  videoOk: z.string().optional(),
  textesOk: z.boolean().optional(),
  descriptionFr: z.string().optional(),
  descriptionNl: z.string().optional(),
  descriptionEn: z.string().nullable().optional(),
  titreFr: z.string().optional(),
  titreNl: z.string().optional(),
  titreEn: z.string().nullable().optional(),
  pointsForts: z.string().nullable().optional(),
  videoYoutubeUrl: z.string().nullable().optional(),
  alerteTexte: z.string().nullable().optional(),
  photos: z.array(z.object({
    id: z.string(),
    path: z.string(),
    url: z.string(),
  })).nullable().optional(),
  // ─── Fiche VENTE (migration 096 — chantier « Catalogue Sales » v1) ───
  // .nullable().optional() OBLIGATOIRE partout : rows existantes = NULL.
  evaluationInterne: z.string().nullable().optional(),
  patrimoineProtege: z.string().nullable().optional(),
  destinationBatiment: z.string().nullable().optional(),
  syndic: z.string().nullable().optional(),
  partiesCopropriete: z.string().nullable().optional(),
  typeCuisine: z.string().nullable().optional(),
  doubleVitrage: z.string().nullable().optional(),
  chassisMateriau: z.string().nullable().optional(),
  chauffageType: z.string().nullable().optional(),
  chauffageEnergie: z.string().nullable().optional(),
  chambresDetail: z.array(SurfaceOrientationSchema).nullable().optional(),
  egouts: z.string().nullable().optional(),
  raccordElectricite: z.string().nullable().optional(),
  raccordGaz: z.string().nullable().optional(),
  raccordEau: z.string().nullable().optional(),
  citerneMazout: z.string().nullable().optional(),
  compteurSepElec: z.string().nullable().optional(),
  compteurSepGaz: z.string().nullable().optional(),
  compteurSepEau: z.string().nullable().optional(),
  borneVehiculeElec: z.string().nullable().optional(),
  prixM2: z.string().nullable().optional(),
  masquerPrix: z.string().nullable().optional(),
  immeubleRapport: z.string().nullable().optional(),
  revenuCadastral: z.string().nullable().optional(),
  rcIndexe: z.string().nullable().optional(),
  precompteImmobilier: z.string().nullable().optional(),
  sousRegimeTva: z.string().nullable().optional(),
  loyerHorsCharges: z.string().nullable().optional(),
  dernierLoyer: z.string().nullable().optional(),
  dernierLoyerDate: z.string().nullable().optional(),
  proximite: z.record(
    z.string(),
    z.object({ present: z.string(), distance: z.string() })
  ).nullable().optional(),
  venteDisponibiliteDate: z.string().nullable().optional(),
  permisConstruire: z.string().nullable().optional(),
  // Salle à manger (oui/non) — fiche vente, Intérieur (migration 137, 2026-09-16).
  salleAManger: z.string().nullable().optional(),
  planCadastral: z.string().nullable().optional(),
  permisLotissement: z.string().nullable().optional(),
  droitPreemption: z.string().nullable().optional(),
  droitPreferenceLocataire: z.string().nullable().optional(),
  infractionUrbanistique: z.string().nullable().optional(),
  destinationUrbanistique: z.string().nullable().optional(),
  zoneInondable: z.string().nullable().optional(),
  certifInondation: z.string().nullable().optional(),
  pScore: z.string().nullable().optional(),
  gScore: z.string().nullable().optional(),
  registreFlamandConsulte: z.string().nullable().optional(),
  mesureJudiciaire: z.string().nullable().optional(),
  terrasses: z.array(SurfaceOrientationSchema).nullable().optional(),
  orientationJardin: z.string().nullable().optional(),
  certificatAmiante: z.string().nullable().optional(),
  construitAvant2001: z.string().nullable().optional(),
  certificatAmianteDate: z.string().nullable().optional(),
  attestationSol: z.string().nullable().optional(),
  consoTheorique: z.string().nullable().optional(),
  pebMin: z.string().nullable().optional(),
  pebMax: z.string().nullable().optional(),
  pebDate: z.string().nullable().optional(),
  inspectionElecValide: z.string().nullable().optional(),
  certifElecDate: z.string().nullable().optional(),
  renovationObligatoire: z.string().nullable().optional(),
  visibiliteImmoweb: z.string().nullable().optional(),
  publication: z.string().optional(),
  type: z.string().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  last_published_at: z.string().nullable().optional(),
  /** Dernier changement d'un champ contenu portail (trigger migration 066). Lecture seule. */
  content_updated_at: z.string().nullable().optional(),
  /**
   * Source de creation du bien. Default 'app' pour les biens crees via V1/V2.
   * 'publi_temp' pour les biens crees via le module /publi-temp (admin externe).
   * Cf. migration 059.
   */
  origin: z.string().optional(),
});

export type BienRow = z.infer<typeof BienSchema>;

/**
 * Schéma pour UPDATE : toutes les colonnes optionnelles, plus de validation
 * sur l'ID (fourni séparément dans l'appel RPC).
 */
export const BienUpdateSchema = BienSchema.omit({ id: true, created_at: true, updated_at: true, last_published_at: true, content_updated_at: true }).partial();
export type BienUpdate = z.infer<typeof BienUpdateSchema>;
